import React, { useState, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// AmpVideosAdmin — admin tab for Feature #142
//
// Props:
//   api       — pre-configured axios instance (from SiteAdminScreen)
//   readOnly  — boolean
// ─────────────────────────────────────────────────────────────────────────────

const DAYS_AHEAD = 14;

function dateOffset(offset) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function fmtDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function extractVideoId(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

const AmpVideosAdmin = ({ api, readOnly }) => {
  const [subTab, setSubTab] = useState('schedule');

  // Schedule state
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Full history (for duplicate detection)
  const [videoHistory, setVideoHistory] = useState([]);

  // Today state
  const [todayVideos, setTodayVideos] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const [activating, setActivating] = useState(false);

  // Add-video form state
  const [addingDate, setAddingDate] = useState(null);
  const [formUrl, setFormUrl] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [removeLoading, setRemoveLoading] = useState(null);
  const [activatingEntry, setActivatingEntry] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const showError = (msg) => { setError(msg); setSuccessMsg(null); };
  const showSuccess = (msg) => { setSuccessMsg(msg); setError(null); };

  // ── Data loading ───────────────────────────────────────────────────────────

  const loadSchedule = useCallback(async () => {
    setScheduleLoading(true);
    setError(null);
    try {
      const res = await api.get(`/amp-videos/schedule?days=${DAYS_AHEAD}`);
      setSchedule(res.data);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load schedule');
    } finally {
      setScheduleLoading(false);
    }
  }, [api]);

  const loadToday = useCallback(async () => {
    setTodayLoading(true);
    try {
      const res = await api.get('/amp-videos/today');
      setTodayVideos(res.data);
    } catch {
      // non-critical
    } finally {
      setTodayLoading(false);
    }
  }, [api]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/amp-videos/history');
      setVideoHistory(res.data);
    } catch {
      // non-critical — just means duplicate detection won't work
    }
  }, [api]);

  useEffect(() => {
    loadSchedule();
    loadToday();
    loadHistory();
  }, [loadSchedule, loadToday, loadHistory]);

  // ── Duplicate detection ────────────────────────────────────────────────────

  // Returns { scheduled_date, youtube_title, youtube_channel, topic_id } for the
  // most-recent prior entry with the same YouTube video ID, or null if none.
  const findDuplicate = (url) => {
    const id = extractVideoId(url);
    if (!id || !videoHistory.length) return null;
    return videoHistory.find(h => extractVideoId(h.youtube_url) === id) || null;
  };

  // ── Preview (oEmbed) ───────────────────────────────────────────────────────

  const handlePreview = async () => {
    if (!formUrl.trim()) return;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);
    try {
      const res = await api.get(`/amp-videos/preview?url=${encodeURIComponent(formUrl.trim())}`);
      setPreview(res.data);
      if (!formTitle && res.data.title) setFormTitle(res.data.title);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Could not load video info — check the URL');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Submit new video ───────────────────────────────────────────────────────

  const handleAddVideo = async () => {
    if (!formUrl.trim() || !addingDate) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/amp-videos/schedule', {
        youtube_url: formUrl.trim(),
        scheduled_date: addingDate,
        title: formTitle.trim() || undefined,
      });
      showSuccess(`Video scheduled for ${fmtDate(addingDate)}`);
      setAddingDate(null);
      setFormUrl('');
      setFormTitle('');
      setPreview(null);
      await loadSchedule();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to schedule video');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelAdd = () => {
    setAddingDate(null);
    setFormUrl('');
    setFormTitle('');
    setPreview(null);
    setPreviewError(null);
  };

  // ── Activate a single entry (any date) ──────────────────────────────────────

  const handleActivateEntry = async (id) => {
    setActivatingEntry(id);
    setError(null);
    try {
      await api.post(`/amp-videos/schedule/${id}/activate`);
      showSuccess('Video activated and posted to the discussion group!');
      await Promise.all([loadSchedule(), loadToday()]);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to activate');
    } finally {
      setActivatingEntry(null);
    }
  };

  // ── Remove ─────────────────────────────────────────────────────────────────

  const handleRemove = async (id) => {
    setRemoveLoading(id);
    setError(null);
    try {
      await api.delete(`/amp-videos/schedule/${id}`);
      showSuccess('Entry removed');
      await loadSchedule();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to remove entry');
    } finally {
      setRemoveLoading(null);
    }
  };

  // ── Activate today ─────────────────────────────────────────────────────────

  const handleActivateToday = async () => {
    setActivating(true);
    setError(null);
    try {
      const res = await api.post('/amp-videos/activate-today');
      if (res.data.count > 0) {
        showSuccess(`${res.data.count} video${res.data.count === 1 ? '' : 's'} activated!`);
      } else {
        showSuccess('No pending videos for today (all already activated, or none scheduled).');
      }
      await Promise.all([loadToday(), loadSchedule()]);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to activate');
    } finally {
      setActivating(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const btnPrimary = { backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.9rem' };
  const btnDanger  = { backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.85rem' };
  const btnGhost   = { background: 'none', border: '1px solid #007bff', color: '#007bff', borderRadius: '4px', padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.85rem' };
  const cardStyle  = { backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' };
  const inputStyle = { width: '100%', maxWidth: '480px', padding: '0.45rem 0.65rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' };

  // Build the date slots for the schedule table
  const dateSlots = Array.from({ length: DAYS_AHEAD }, (_, i) => {
    const d = dateOffset(i);
    const videos = schedule.filter(s => {
      const sd = typeof s.scheduled_date === 'string' ? s.scheduled_date.slice(0, 10) : new Date(s.scheduled_date).toISOString().slice(0, 10);
      return sd === d;
    });
    return { date: d, videos };
  });

  const today = dateOffset(0);

  return (
    <div>
      <h3 style={{ marginBottom: '0.5rem' }}>Amp Videos</h3>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        Schedule YouTube video reviews to drop daily in the Amp Videos discussion group.
        Multiple videos per day are supported — they all activate at midnight UTC.
      </p>

      {/* Sub-tab nav */}
      <div style={{ display: 'flex', gap: '1.5rem', borderBottom: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
        {[
          { key: 'schedule', label: `${DAYS_AHEAD}-Day Queue` },
          { key: 'today',    label: "Today's Videos" },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            background: 'none', border: 'none', padding: '0.75rem 0',
            borderBottom: subTab === t.key ? '2px solid #007bff' : '2px solid transparent',
            color: subTab === t.key ? '#007bff' : '#666',
            fontWeight: subTab === t.key ? 'bold' : 'normal',
            cursor: 'pointer', fontSize: '0.95rem',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Feedback */}
      {error && (
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {successMsg && (
        <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          {successMsg}
        </div>
      )}

      {/* ── Schedule queue tab ── */}
      {subTab === 'schedule' && (
        <div>
          {scheduleLoading ? (
            <p style={{ color: '#666' }}>Loading schedule...</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem 0.75rem' }}>Date</th>
                  <th style={{ padding: '0.5rem 0.75rem' }}>Videos</th>
                  {!readOnly && <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }} />}
                </tr>
              </thead>
              <tbody>
                {dateSlots.map(({ date, videos }) => (
                  <React.Fragment key={date}>
                    {/* Date header row */}
                    <tr style={{ backgroundColor: date === today ? '#fff9e6' : '#f8f9fa', borderBottom: videos.length === 0 ? '1px solid #dee2e6' : 'none' }}>
                      <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap', fontWeight: date === today ? 'bold' : '600', verticalAlign: 'top', width: '140px' }}>
                        {fmtDate(date)}
                        {date === today && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#856404', background: '#fff3cd', borderRadius: '3px', padding: '1px 4px' }}>TODAY</span>}
                      </td>
                      <td colSpan={readOnly ? 1 : 2} style={{ padding: '0.4rem 0.75rem' }}>
                        {videos.length === 0 && (
                          <span style={{ color: '#aaa', fontStyle: 'italic', fontSize: '0.875rem' }}>No videos scheduled</span>
                        )}
                      </td>
                    </tr>

                    {/* One row per scheduled video for this date */}
                    {videos.map((v, idx) => {
                      const videoId = extractVideoId(v.youtube_url);
                      const isLast = idx === videos.length - 1;
                      // Find a prior entry for the same video ID (exclude self)
                      const priorEntry = videoHistory.find(
                        h => h.video_schedule_id !== v.video_schedule_id && extractVideoId(h.youtube_url) === videoId
                      );
                      const priorDate = priorEntry
                        ? (typeof priorEntry.scheduled_date === 'string' ? priorEntry.scheduled_date.slice(0, 10) : new Date(priorEntry.scheduled_date).toISOString().slice(0, 10))
                        : null;
                      return (
                        <tr key={v.video_schedule_id} style={{ borderBottom: isLast ? '1px solid #dee2e6' : 'none', backgroundColor: date === today ? '#fffdf0' : '#fff' }}>
                          <td style={{ padding: '0.4rem 0.75rem', width: '140px' }} />
                          <td style={{ padding: '0.4rem 0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {videoId && (
                                <img
                                  src={`https://img.youtube.com/vi/${videoId}/default.jpg`}
                                  alt=""
                                  style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
                                />
                              )}
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{v.youtube_title || v.youtube_url}</div>
                                {v.youtube_channel && <div style={{ color: '#888', fontSize: '0.78rem' }}>{v.youtube_channel}</div>}
                                {priorDate && (
                                  <div style={{ fontSize: '0.73rem', color: '#856404', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '3px', padding: '1px 5px', display: 'inline-block', marginTop: '2px', marginBottom: '2px' }}>
                                    ⚠️ Rerun — previously posted {fmtDate(priorDate)}
                                    {priorEntry.topic_id ? <span> · <a href={`/discussions/topic/${priorEntry.topic_id}`} target="_blank" rel="noreferrer" style={{ color: '#856404' }}>prior thread</a></span> : null}
                                  </div>
                                )}
                                {v.topic_id
                                  ? <span style={{ fontSize: '0.75rem', color: '#28a745', display: 'block' }}>● activated · <a href={`/discussions/topic/${v.topic_id}`} target="_blank" rel="noreferrer">view thread</a></span>
                                  : <span style={{ fontSize: '0.75rem', color: '#868e96', display: 'block' }}>scheduled</span>
                                }
                              </div>
                            </div>
                          </td>
                          {!readOnly && (
                            <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                              {!v.topic_id && (
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                  <button
                                    style={{ ...btnPrimary, fontSize: '0.8rem', padding: '0.25rem 0.65rem' }}
                                    disabled={activatingEntry === v.video_schedule_id}
                                    onClick={() => handleActivateEntry(v.video_schedule_id)}
                                  >
                                    {activatingEntry === v.video_schedule_id ? '…' : 'Activate Now'}
                                  </button>
                                  <button
                                    style={btnDanger}
                                    disabled={removeLoading === v.video_schedule_id}
                                    onClick={() => handleRemove(v.video_schedule_id)}
                                  >
                                    {removeLoading === v.video_schedule_id ? '…' : 'Remove'}
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* "+ Add Video" row for each date */}
                    {!readOnly && (
                      <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td />
                        <td colSpan={2} style={{ padding: '0.35rem 0.75rem 0.6rem' }}>
                          {addingDate === date ? (
                            /* Inline add form */
                            <div style={{ background: '#f0f4ff', border: '1px solid #c5d6f5', borderRadius: '6px', padding: '1rem', maxWidth: '560px' }}>
                              <div style={{ marginBottom: '0.6rem' }}>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '3px' }}>YouTube URL</label>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <input
                                    type="url"
                                    value={formUrl}
                                    onChange={e => { setFormUrl(e.target.value); setPreview(null); setPreviewError(null); }}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    style={{ ...inputStyle, flex: 1 }}
                                    onKeyDown={e => e.key === 'Enter' && handlePreview()}
                                  />
                                  <button style={btnGhost} onClick={handlePreview} disabled={!formUrl.trim() || previewLoading}>
                                    {previewLoading ? '…' : 'Preview'}
                                  </button>
                                </div>
                                {previewError && <div style={{ color: '#721c24', fontSize: '0.8rem', marginTop: '4px' }}>{previewError}</div>}
                                {(() => {
                                  const dup = formUrl.trim() && extractVideoId(formUrl) ? findDuplicate(formUrl) : null;
                                  if (!dup) return null;
                                  const dupDate = typeof dup.scheduled_date === 'string' ? dup.scheduled_date.slice(0, 10) : new Date(dup.scheduled_date).toISOString().slice(0, 10);
                                  return (
                                    <div style={{ marginTop: '6px', padding: '6px 10px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '0.8rem', color: '#856404' }}>
                                      ⚠️ <strong>Already posted</strong> — this video was scheduled on {fmtDate(dupDate)}
                                      {dup.youtube_title ? ` as "${dup.youtube_title}"` : ''}
                                      {dup.topic_id ? <span> · <a href={`/discussions/topic/${dup.topic_id}`} target="_blank" rel="noreferrer" style={{ color: '#856404' }}>view thread</a></span> : null}
                                    </div>
                                  );
                                })()}
                              </div>

                              {preview && (
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '0.6rem', alignItems: 'center', background: '#fff', borderRadius: '4px', padding: '8px', border: '1px solid #e0e0e0' }}>
                                  <img src={preview.thumbnail_url} alt="" style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{preview.title || '(no title)'}</div>
                                    {preview.channel && <div style={{ color: '#888', fontSize: '0.78rem' }}>{preview.channel}</div>}
                                  </div>
                                </div>
                              )}

                              <div style={{ marginBottom: '0.6rem' }}>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '3px' }}>Title override <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span></label>
                                <input
                                  type="text"
                                  value={formTitle}
                                  onChange={e => setFormTitle(e.target.value)}
                                  placeholder="Leave blank to use YouTube title"
                                  style={inputStyle}
                                />
                              </div>

                              <div style={{ display: 'flex', gap: '8px', marginTop: '0.75rem' }}>
                                <button
                                  style={{ ...btnPrimary, opacity: (!formUrl.trim() || submitting) ? 0.6 : 1 }}
                                  onClick={handleAddVideo}
                                  disabled={!formUrl.trim() || submitting}
                                >
                                  {submitting ? 'Scheduling…' : `Schedule for ${fmtDate(date)}`}
                                </button>
                                <button style={{ ...btnGhost, borderColor: '#6c757d', color: '#6c757d' }} onClick={handleCancelAdd}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              style={{ ...btnGhost, fontSize: '0.8rem' }}
                              onClick={() => { handleCancelAdd(); setAddingDate(date); }}
                            >
                              + Add Video
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Today tab ── */}
      {subTab === 'today' && (
        <div>
          {!readOnly && (
            <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
              <p style={{ margin: '0 0 0.75rem', color: '#555', fontSize: '0.9rem' }}>
                Manually trigger today's scheduled videos — normally this happens automatically at midnight UTC.
              </p>
              <button style={btnPrimary} onClick={handleActivateToday} disabled={activating}>
                {activating ? 'Activating…' : 'Activate Today\'s Videos'}
              </button>
            </div>
          )}

          {todayLoading ? (
            <p style={{ color: '#666' }}>Loading...</p>
          ) : todayVideos.length === 0 ? (
            <p style={{ color: '#888', fontStyle: 'italic' }}>No videos activated today yet.</p>
          ) : (
            <div>
              <p style={{ color: '#555', marginBottom: '1rem', fontSize: '0.9rem' }}>{todayVideos.length} video{todayVideos.length === 1 ? '' : 's'} activated today:</p>
              {todayVideos.map(v => {
                const videoId = extractVideoId(v.youtube_url);
                const msgCount = parseInt(v.message_count) || 0;
                return (
                  <div key={v.video_schedule_id} style={{ ...cardStyle, display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    {videoId && (
                      <img
                        src={`https://img.youtube.com/vi/${videoId}/default.jpg`}
                        alt=""
                        style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '3px' }}>{v.youtube_title || v.youtube_url}</div>
                      {v.youtube_channel && <div style={{ color: '#888', fontSize: '0.82rem', marginBottom: '6px' }}>{v.youtube_channel}</div>}
                      {v.topic_id && (
                        <a href={`/discussions/topic/${v.topic_id}`} target="_blank" rel="noreferrer" style={{ fontSize: '0.85rem' }}>
                          View discussion thread
                        </a>
                      )}
                      {msgCount > 0 && <span style={{ marginLeft: '0.5rem', fontSize: '0.82rem', color: '#888' }}>· {msgCount} repl{msgCount === 1 ? 'y' : 'ies'}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AmpVideosAdmin;
